import { useEffect, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { api } from '../api';

type QueueItem = {
  id: string;
  status: string;
  submitted_at: string | null;
  started_at: string;
  demo_mode: boolean;
  first_name: string | null;
  email: string | null;
  industry: string | null;
  qualifier_choice: string | null;
  path: string | null;
  synthesis_status: 'pending' | 'generating' | 'ready' | 'failed';
};

const STATUS_OPTIONS = [
  { value: 'submitted', label: 'Submitted' },
  { value: 'all', label: 'All' },
  { value: 'in_progress', label: 'In progress' },
  { value: 'rejected', label: 'Rejected' },
  { value: 'delivered', label: 'Delivered' },
];

const PATH_OPTIONS = [
  { value: '', label: 'All paths' },
  { value: 'pathA', label: 'Founder-Led' },
  { value: 'pathB', label: 'Management-Led' },
];

const INDUSTRY_OPTIONS = [
  { value: '', label: 'All industries' },
  { value: 'professional_services', label: 'Professional services' },
  { value: 'real_estate', label: 'Real estate' },
  { value: 'health_wellness', label: 'Health & wellness' },
  { value: 'ecommerce_retail', label: 'E-commerce / retail' },
  { value: 'trades_manufacturing', label: 'Trades / mfg' },
  { value: 'saas_software', label: 'SaaS' },
  { value: 'other', label: 'Other' },
];

export default function Queue() {
  const [search, setSearch] = useSearchParams();
  const status = search.get('status') || 'submitted';
  const path = search.get('path') || '';
  const industry = search.get('industry') || '';
  const sort = search.get('sort') || 'oldest';

  const [items, setItems] = useState<QueueItem[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;
    setItems(null);
    setError(null);
    (async () => {
      try {
        const result = await api.queue({ status, path: path || undefined, industry: industry || undefined, sort });
        if (!cancelled) setItems(result.items);
      } catch (err: any) {
        if (!cancelled) setError(err?.message || 'Could not load queue.');
      }
    })();
    return () => { cancelled = true; };
  }, [status, path, industry, sort]);

  function update(key: string, value: string) {
    const next = new URLSearchParams(search);
    if (value) next.set(key, value); else next.delete(key);
    setSearch(next, { replace: true });
  }

  return (
    <div className="max-w-6xl">
      <div className="flex items-baseline justify-between mb-6">
        <h1 className="font-display text-3xl font-medium text-ink">Queue</h1>
        {items && <span className="font-mono text-[11px] uppercase tracking-widest text-ink-light">{items.length} item{items.length === 1 ? '' : 's'}</span>}
      </div>

      {/* Filter bar */}
      <div className="flex flex-wrap gap-3 mb-6 pb-4 border-b border-rule">
        <Select label="Status" value={status} options={STATUS_OPTIONS} onChange={(v) => update('status', v)} />
        <Select label="Path" value={path} options={PATH_OPTIONS} onChange={(v) => update('path', v)} />
        <Select label="Industry" value={industry} options={INDUSTRY_OPTIONS} onChange={(v) => update('industry', v)} />
        <Select
          label="Sort"
          value={sort}
          options={[{ value: 'oldest', label: 'Oldest first' }, { value: 'newest', label: 'Newest first' }]}
          onChange={(v) => update('sort', v)}
        />
      </div>

      {error && <p className="font-serif text-accent">{error}</p>}

      {!items && !error && (
        <p className="font-mono text-[11px] uppercase tracking-wider text-ink-light">Loading…</p>
      )}

      {items && items.length === 0 && (
        <p className="font-serif text-ink-light italic">Queue is clear.</p>
      )}

      {items && items.length > 0 && (
        <div className="overflow-x-auto border border-rule bg-paper">
          <table className="w-full font-serif text-sm">
            <thead>
              <tr className="bg-cream-dark text-left">
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Status</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Operator</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Industry</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Path</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Q</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Submitted</th>
                <th className="px-4 py-3 font-mono text-[11px] uppercase tracking-wider text-ink-light">Synthesis</th>
                <th className="px-4 py-3"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id} className="border-t border-rule hover:bg-cream-dark transition-colors">
                  <td className="px-4 py-3"><StatusBadge status={item.status} demo={item.demo_mode} /></td>
                  <td className="px-4 py-3 text-ink">{item.first_name || <span className="text-ink-light italic">no name</span>}</td>
                  <td className="px-4 py-3 text-ink-soft">{industryShort(item.industry)}</td>
                  <td className="px-4 py-3 text-ink-soft font-mono text-[12px]">{item.path === 'pathA' ? 'FL' : item.path === 'pathB' ? 'ML' : '—'}</td>
                  <td className="px-4 py-3 text-ink-soft font-mono text-[12px]">{item.qualifier_choice || '—'}</td>
                  <td className="px-4 py-3 text-ink-light font-mono text-[12px]" title={item.submitted_at || ''}>{item.submitted_at ? relativeTime(new Date(item.submitted_at)) : '—'}</td>
                  <td className="px-4 py-3"><SynthBadge status={item.synthesis_status} /></td>
                  <td className="px-4 py-3 text-right">
                    <Link
                      to={`/review/${item.id}`}
                      className="font-mono text-[11px] uppercase tracking-wider text-accent hover:underline whitespace-nowrap"
                    >
                      Review →
                    </Link>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function Select({ label, value, options, onChange }: {
  label: string;
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
}) {
  return (
    <label className="flex flex-col">
      <span className="font-mono text-[10px] uppercase tracking-widest text-ink-light mb-1">{label}</span>
      <select
        value={value}
        onChange={(e) => onChange(e.target.value)}
        className="bg-paper border border-rule px-3 py-1.5 font-serif text-sm text-ink focus:outline-none focus:border-accent"
      >
        {options.map((o) => (
          <option key={o.value || 'none'} value={o.value}>{o.label}</option>
        ))}
      </select>
    </label>
  );
}

function StatusBadge({ status, demo }: { status: string; demo: boolean }) {
  if (demo) return <span className="font-mono text-[10px] uppercase tracking-widest text-gold-warm bg-cream-dark px-2 py-1">demo</span>;
  const map: Record<string, { color: string; bg: string; text: string }> = {
    submitted: { color: '#1e40af', bg: '#dbeafe', text: 'submitted' },
    in_progress: { color: '#6b7280', bg: '#f3f4f6', text: 'in progress' },
    delivered: { color: '#065f46', bg: '#d1fae5', text: 'delivered' },
    rejected: { color: '#991b1b', bg: '#fee2e2', text: 'rejected' },
    approved: { color: '#1e40af', bg: '#dbeafe', text: 'approved' },
  };
  const s = map[status] || { color: '#6b7280', bg: '#f3f4f6', text: status };
  return (
    <span
      className="font-mono text-[10px] uppercase tracking-widest px-2 py-1"
      style={{ background: s.bg, color: s.color }}
    >
      {s.text}
    </span>
  );
}

function SynthBadge({ status }: { status: 'pending' | 'generating' | 'ready' | 'failed' }) {
  const map = {
    pending: { color: '#6b7280', bg: '#f3f4f6', text: 'pending' },
    generating: { color: '#6b7280', bg: '#f3f4f6', text: 'generating' },
    ready: { color: '#065f46', bg: '#d1fae5', text: 'ready' },
    failed: { color: '#991b1b', bg: '#fee2e2', text: 'failed' },
  };
  const s = map[status] || map.pending;
  return (
    <span
      className="font-mono text-[10px] uppercase tracking-widest px-2 py-1"
      style={{ background: s.bg, color: s.color }}
    >
      {s.text}
    </span>
  );
}

function industryShort(key: string | null): string {
  if (!key) return '—';
  const map: Record<string, string> = {
    professional_services: 'professional services',
    real_estate: 'real estate',
    health_wellness: 'health & wellness',
    ecommerce_retail: 'e-commerce',
    trades_manufacturing: 'trades',
    saas_software: 'SaaS',
    other: 'other',
  };
  return map[key] || key;
}

function relativeTime(d: Date): string {
  const ms = Date.now() - d.getTime();
  const min = Math.round(ms / 60000);
  if (min < 1) return 'just now';
  if (min < 60) return `${min}m ago`;
  const h = Math.round(min / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.round(h / 24);
  if (days < 14) return `${days}d ago`;
  return d.toISOString().slice(0, 10);
}
